import React from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { REPORT_ACCENT } from './reportTheme';
import { bucketByDaypart, shiftHoursToLocal } from './dayparts';
import { rangeQuery } from './reportRange';
import RangeBadge from './RangeBadge';

// CTV-445: daypart — impressions grouped into the named broadcast dayparts. Data
// from the selfservice-api GET /submissions/:id/daypart (24 zero-filled hours);
// we bucket to dayparts client-side via bucketByDaypart.

interface HourDatum {
  hour: number;
  impressions: number;
  completes: number;
}

interface DaypartResponse {
  total: { impressions: number; completes: number };
  byHour: HourDatum[];
  // Campaign market zone for display. null → hours shown as UTC.
  timezone?: string | null;
  offsetMinutes?: number | null;
  timezoneLabel?: string | null;
}

interface DaypartChartProps {
  submissionId: string;
  accentColor?: string;
  secondaryColor?: string;
  from?: string | null;
  to?: string | null;
  rangeLabel?: string | null;
}

const DaypartChart: React.FC<DaypartChartProps> = ({ submissionId, accentColor, secondaryColor, from, to, rangeLabel }) => {
  const accent = accentColor || REPORT_ACCENT;
  const marker = secondaryColor || accent;
  const [data, setData] = React.useState<DaypartResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    // Keep the current bars on screen (dimmed) while the new range loads.
    setLoading(true);
    setError(null);
    apiClient
      .get<DaypartResponse>(`/submissions/${submissionId}/daypart${rangeQuery({ from: from ?? null, to: to ?? null })}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load daypart data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, from, to]);

  const header = (
    <div className="flex items-center justify-between mb-2 px-1 gap-3">
      <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
        <span className="w-0.5 h-3 rounded-full" style={{ backgroundColor: marker }} />
        Daypart
        <RangeBadge label={rangeLabel} accentColor={accent} />
      </div>
    </div>
  );

  // Spinner only on the first load; range changes keep the prior bars.
  if (data === null) {
    return (
      <div>
        {header}
        <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-8 text-center text-[12px] text-slate-400">
          <Loader2 className="inline-block w-3 h-3 mr-1 animate-spin" />
          Loading daypart…
        </div>
      </div>
    );
  }

  const totalImps = data?.total?.impressions ?? 0;
  if (error || !data || totalImps === 0) {
    return (
      <div>
        {header}
        <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-8 text-center text-[13px] text-slate-600">
          {error ? `Couldn't load daypart data: ${error}` : 'No daypart data yet.'}
        </div>
      </div>
    );
  }

  const localHours = shiftHoursToLocal(data.byHour ?? [], data.offsetMinutes);
  const buckets = bucketByDaypart(localHours);
  // Bars are each window's share of total plays (sum to 100%), so the full track
  // represents all plays — matching the geographic section's "share of plays".
  const total = Math.max(1, buckets.reduce((s, b) => s + b.impressions, 0));
  const tzLabel = data.timezoneLabel || 'UTC';

  return (
    <div className="flex flex-col h-full">
      {header}
      <div
        className={`rounded-2xl border border-slate-200/80 bg-white overflow-hidden transition-opacity duration-300 flex-1 flex flex-col min-h-0 ${loading ? 'opacity-60' : 'opacity-100'}`}
      >
        <div className="px-5 pt-4 pb-2 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" style={{ color: accent }} />
          <span className="text-[12px] text-slate-600">Plays by daypart ({tzLabel})</span>
        </div>

        {/* Column labels (header). */}
        <div className="px-5 py-2.5 bg-slate-50/60 border-b border-slate-200/70 text-[10px] uppercase tracking-[0.12em] text-slate-400 flex items-center gap-3">
          <span className="w-28 shrink-0 text-right">Window</span>
          <span className="flex-1">Share of plays</span>
          <span className="w-16 shrink-0 text-right">Plays</span>
          <span className="w-12 shrink-0 text-right">Compl.</span>
        </div>

        {/* Vertical layout: one horizontal bar per daypart window. The rows flex
            to fill the card so the daypart height matches the (often taller)
            delivery report beside it — the bars grow rather than leaving a gap. */}
        <div className="px-5 pt-1 pb-4 flex-1 flex flex-col gap-2.5 min-h-0">
          {buckets.map((b) => {
            const widthPct = (b.impressions / total) * 100;
            const rate = b.impressions > 0 ? Math.min(100, Math.round((b.completes / b.impressions) * 100)) : 0;
            return (
              <div key={b.label} className="flex-1 min-h-[14px] flex items-center gap-3" data-daypart-row>
                <div className="w-28 shrink-0 text-right leading-tight">
                  <div className="text-[11px] text-slate-700">{b.label}</div>
                  <div className="text-[10px] tabular-nums text-slate-400">{b.hours}</div>
                </div>
                <div className="flex-1 min-w-0 h-full max-h-9 rounded bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded"
                    style={{ width: `${b.impressions > 0 ? Math.max(2, widthPct) : 0}%`, backgroundColor: accent, opacity: 0.9 }}
                  />
                </div>
                <div className="w-16 shrink-0 text-right text-[12px] tabular-nums text-slate-800 font-semibold">
                  {b.impressions.toLocaleString('en-US')}
                </div>
                <div className="w-12 shrink-0 text-right text-[11px] tabular-nums text-slate-400">{rate}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DaypartChart;
